import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import {
  Box,
  costFormater,
  NavigationRightIcon,
  Preloader,
  PriceInput,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import { Enrollment } from '@common-fe/common-fe';
import _ from 'lodash';

import AppButton from '@/components/controls/AppButton';
import { SuccessModal } from '@/components/elements';
import ServerErrorModal from '@/modules/investments/modal/ServerErrorModal';

import { useAutoInvestmentExist } from '../../AutoInvestment/hooks';
import { useInvestmentsHoldings, useInvestmentsValidation } from '../../hooks';
import { InvestmentCardSellBlock } from '../../InvestmentCardBlock';
import { useTradeInvestmentsQuery } from '../../queries';
import DisclosureFooter from '../DisclosureFooter';
import { useFundingAmountStore } from '../stores/useFundingAmount.store';

import ConfirmFullLiquidation from './ConfirmFullLiquidation';
import ConfirmSellModal from './ConfirmSellModal';
import FullLIquidationToggle from './FullLiquidationToggle';

interface Props {
  accountId: string;
  onCancel: () => void;
  currentMyAccount: Enrollment;
}

const FULL_LIQUIDATION_PERCENTS = 0.95;

const FundingAmount: React.FC<Props> = ({ onCancel, currentMyAccount, accountId }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [isFullLiquidation, setFullLiquidation] = useState(false);

  const { values, handleSetValues } = useFundingAmountStore();

  const [investingAmount, setInvestingAmount] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [sellModalVisible, setSellModalVisible] = useState(false);
  const [showServerErrorModal, setShowServerErrorModal] = useState(false);

  const { details } = useInvestmentsHoldings(accountId);
  const [successVisible, setSuccessVisible] = useState(false);
  const formattedMyAccount = useMemo(
    () => ({
      ...currentMyAccount,
      available: details.portfolioBalance || 0,
    }),
    [currentMyAccount, details.portfolioBalance]
  );
  const availableBalance = useMemo(() => {
    return formattedMyAccount?.available;
  }, [formattedMyAccount?.available]);

  const { isLoading, handleSave } = useTradeInvestmentsQuery({ isSellMode: true });
  const isAppliedFullLiquidation = useMemo(() => {
    const numberAmount = _.toNumber(investingAmount);
    const currentValue = numberAmount / availableBalance;
    return currentValue >= FULL_LIQUIDATION_PERCENTS;
  }, [availableBalance, investingAmount]);

  const handleSellInvestments = useCallback(async () => {
    try {
      await handleSave({
        isFullLiquidation: isFullLiquidation || isAppliedFullLiquidation,
        amount: _.toNumber(investingAmount),
      });

      setSuccessVisible(true);
      handleSetValues({
        fundingAmount: investingAmount,
      });
    } catch {
      setShowServerErrorModal(true);
    }
  }, [handleSave, handleSetValues, investingAmount, isAppliedFullLiquidation, isFullLiquidation]);

  const { error } = useInvestmentsValidation({
    investingAmount: isFullLiquidation ? `${availableBalance}` : investingAmount,
    availableToInvest: availableBalance,
    isSellingMode: true,
    minimumInvestmentAmount: currentMyAccount?.minimumInvestmentAmount,
  });
  const handleSubmit = useCallback(async () => {
    setShowErrors(true);
    if (error) {
      return null;
    }

    setSellModalVisible(true);
    return null;
  }, [error]);

  useEffect(() => {
    if (values?.fundingAmount) {
      setInvestingAmount(values?.fundingAmount);
    }
  }, [values]);

  const { isAutoInvestmentsExist } = useAutoInvestmentExist();
  return (
    <Box direction="column" data-testid="Funding-wrapper-id">
      {isAppliedFullLiquidation || isFullLiquidation ? (
        <ConfirmFullLiquidation
          accountId={accountId}
          isLoading={isLoading}
          visible={sellModalVisible && !successVisible}
          onSetVisible={setSellModalVisible}
          onSubmit={handleSellInvestments}
        />
      ) : (
        <ConfirmSellModal
          fundingAmount={investingAmount}
          isLoading={isLoading}
          visible={sellModalVisible && !successVisible}
          onSetVisible={setSellModalVisible}
          onSubmit={handleSellInvestments}
        />
      )}

      {isFullLiquidation || isAppliedFullLiquidation ? (
        <SuccessModal
          testId="success-liquidation"
          header={t('Submitted!')}
          buttonText={t('Go To Investment Dashboard')}
          visible={successVisible}
          onSetVisible={onCancel}
          onSubmit={onCancel}
        />
      ) : (
        <SuccessModal
          testId="success"
          header={`${costFormater(_.toNumber(values.fundingAmount), true)} ${t(
            'trade settlement submitted'
          )}`}
          helptext={
            <Text>
              {/* {t('Trade settlement may take up to 2 days.')}{' '} */}
              {isAutoInvestmentsExist && (
                <Text>
                  {t('Automatic Investments were')}
                  <Text weight="bold">{t(' paused')}.</Text>
                </Text>
              )}
            </Text>
          }
          buttonText={t('Go to Investment Dashboard')}
          visible={successVisible}
          onSetVisible={onCancel}
          onSubmit={onCancel}
        />
      )}

      <ServerErrorModal
        visible={showServerErrorModal}
        onSetVisible={setShowServerErrorModal}
        isLoading={isLoading}
        onSendRequest={handleSellInvestments}
        style={{ zIndex: 10000, position: 'fixed' }}
      />
      <Prompt
        when={!!investingAmount && !successVisible}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      <Box flex="grow">
        <Box direction="column" margin={isMobile ? { horizontal: 'spacing16' } : undefined}>
          <Text size="3xl" color="blockTitle" margin={{ bottom: 's' }} weight={700}>
            {t('Sell Investments')}
          </Text>
          <Text size="large" margin={{ bottom: isMobile ? 'spacing12' : 'spacing24' }}>
            {t('Move money from your investment account to your cash account in a few easy steps.')}
          </Text>
        </Box>
        <Box
          direction={isMobile ? 'column' : 'row'}
          align="center"
          background="module"
          round="moduleRound"
          pad={isMobile ? 'spacing16' : 'spacing24'}
          margin={{ bottom: isMobile ? 'spacing12' : 'spacing24' }}
        >
          <Box data-testid="Enrollment-card-wrapper-id" width={isMobile ? '100%' : { min: '400px' }}>
            {formattedMyAccount && (
              <InvestmentCardSellBlock
                enrollment={formattedMyAccount}
                {...(isMobile
                  ? {
                      width: {
                        min: 'auto',
                      },
                    }
                  : {})}
              />
            )}
          </Box>
          <Box direction="row" align="center" justify="center" margin={{ horizontal: 'spacing12' }}>
            {isMobile ? (
              <Box margin={{ vertical: 'spacing8' }}>
                <NavigationRightIcon style={{ transform: 'rotate(90deg)' }} />
              </Box>
            ) : (
              <NavigationRightIcon />
            )}
          </Box>
          <Box direction="column">
            <Box
              direction="column"
              pad={isMobile ? 'spacing16' : 'spacing24'}
              height={isMobile ? 'auto' : '248px'}
              background="canvas"
              round="container1Round"
              data-testid="invest_more_funds-wrapper"
              border={{ size: 'small', color: 'border2' }}
              elevation="default"
            >
              <Text margin={{ bottom: 'spacing12' }} size="xxlarge">
                {t('Investment to Cash')}
              </Text>
              <Text margin={{ bottom: 'spacing24' }} size="medium">
                {t(
                  'We will transfer this amount from your investment account to your cash account as soon as the trade is executed.'
                )}
              </Text>
              <Box direction={isMobile ? 'column' : 'row'} align={isMobile ? 'start' : 'center'}>
                <Box
                  width={isMobile ? '100%' : '280px'}
                  margin={isMobile ? undefined : { right: 'spacing24' }}
                  data-testid="Price-control-wrapper-id"
                >
                  <PriceInput
                    isClearMode
                    testId="funding-sell_amount"
                    name="Funding amount"
                    placeholder={t('Enter $ amount')}
                    onChange={setInvestingAmount}
                    onIconClick={() => setInvestingAmount('')}
                    value={isFullLiquidation ? `${availableBalance || ''}` : investingAmount}
                    disabled={isLoading || isFullLiquidation}
                    autoFocus
                  />
                </Box>
                <Box margin={isMobile ? { top: 'spacing12' } : undefined}>
                  <FullLIquidationToggle
                    isLiquidation={isFullLiquidation}
                    onSetLiquidation={setFullLiquidation}
                    isApplied={isAppliedFullLiquidation}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box pad={isMobile ? { horizontal: 'spacing16' } : undefined}>
          {error && showErrors && (
            <Box
              align="end"
              width="100%"
              margin={isMobile ? { bottom: 'spacing8' } : { bottom: 'spacing24' }}
            >
              <Text
                data-testId="funding-sell_error"
                size="medium"
                color="danger"
                style={{ lineHeight: '20px' }}
              >
                {t(error)}
              </Text>
            </Box>
          )}
          <Box direction="row" align="center" justify="end">
            <Box direction="row" fill={isMobile} gap="spacing8" justify="end" align="center">
              <Box fill={isMobile}>
                <AppButton
                  fill={isMobile}
                  onClick={() => onCancel()}
                  buttonType="secondary"
                  width="130px"
                  testId="funding-sell_cancel"
                >
                  {t('Cancel')}
                </AppButton>
              </Box>
              <Box fill={isMobile}>
                <AppButton
                  fill={isMobile}
                  type="submit"
                  width="130px"
                  onClick={() => handleSubmit()}
                  disabled={isLoading}
                  testId="funding-sell_submit"
                >
                  {isLoading ? <Preloader color="white" /> : t('Submit')}
                </AppButton>
              </Box>
            </Box>
          </Box>

          <Box pad={{ bottom: 'spacing24' }}>
            <DisclosureFooter />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FundingAmount;
